import { memo, useCallback, useMemo } from 'react';

import { useTrackAgentMessageFeedbackSubmitted } from '@air/analytics';

import {
  WorkspaceAgentMessageFeedbackActions,
  type WorkspaceAgentMessageFeedbackRating,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/feedback/WorkspaceAgentMessageFeedbackActions';
import { usePrivateWorkspaceAgentMessageListContext } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentMessageListContext';
import { getWorkspaceAgentMessageCopyPlainText } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/getWorkspaceAgentMessageCopyPlainText';
import { usePrivateWorkspaceAgentPanelPillClips } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPillClipsProvider';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';

export type PrivateWorkspaceAgentPanelMessageFeedbackProps = {
  message: WorkspaceAgentMessage;
  pills: readonly WorkspaceAgentMessagePill[];
  rating: WorkspaceAgentMessageFeedbackRating | undefined;
};

export const PrivateWorkspaceAgentPanelMessageFeedback = memo(
  ({ message, pills, rating }: PrivateWorkspaceAgentPanelMessageFeedbackProps) => {
    const { onRateMessage } = usePrivateWorkspaceAgentMessageListContext();
    const { getPillClip, getPillClipById } = usePrivateWorkspaceAgentPanelPillClips();
    const { trackAgentMessageFeedbackSubmitted } = useTrackAgentMessageFeedbackSubmitted();

    const copyText = useMemo(
      () =>
        getWorkspaceAgentMessageCopyPlainText({
          content: message.content ?? '',
          getAssetListMemberClipName: (clipId) => getPillClipById(clipId)?.name,
          getPillClipName: (pill) => getPillClip(pill)?.name,
          pills,
        }),
      [getPillClip, getPillClipById, message.content, pills],
    );

    const handleRate = useCallback(
      (nextRating: WorkspaceAgentMessageFeedbackRating) => {
        if (rating === nextRating) {
          return;
        }

        onRateMessage(message.id, nextRating);
        trackAgentMessageFeedbackSubmitted({
          message_id: message.id,
          sentiment: nextRating === 'up' ? 'thumbs_up' : 'thumbs_down',
          session_id: message.sessionId,
        });
      },
      [message.id, message.sessionId, onRateMessage, rating, trackAgentMessageFeedbackSubmitted],
    );

    return (
      <WorkspaceAgentMessageFeedbackActions
        copyText={copyText}
        data-testid="WORKSPACE_AGENT_MESSAGE_FEEDBACK"
        onRate={handleRate}
        rating={rating}
      />
    );
  },
);

PrivateWorkspaceAgentPanelMessageFeedback.displayName = 'PrivateWorkspaceAgentPanelMessageFeedback';
